"use strict";

/**
 * RobustCart: fixed version (idempotent listeners, refresh-safe, async/await).
 * Why: Avoid duplicate handlers after refresh; delegate where possible.
 */
class RobustCart {
  /**
   * @param {HTMLElement} rootEl
   * @param {string} country
   * @param {string} currency
   * @param {string} cartUrl
   * @param {string} cartAddUrl
   * @param {string} cartChangeUrl
   * @param {Function|undefined} recommendedItemAddListener
   * @param {any} recommendedProducts
   */
  constructor(rootEl, country, currency, cartUrl, cartAddUrl, cartChangeUrl, recommendedItemAddListener, recommendedProducts) {
    // --- config/state ---
    this.rootEl = rootEl;
    this.country = country;
    this.currency = currency;
    this.cartUrl = cartUrl;
    this.cartAddUrl = cartAddUrl;
    this.cartChangeUrl = cartChangeUrl;
    this.recommendedItemAddListener = recommendedItemAddListener;
    this.recommendedProducts = recommendedProducts;

    this.scrollPosition = window.scrollY || 0;

    // one-time wiring guards
    this._wired = {
      openLinks: false,
      overlayClose: false,
      boxStop: false,
      docAddToCart: false,
    };

    this.log("Initializing");
    this.addAllListeners();
    this.log("Initialized");
  }

  // ---- utils ----
  log = (msg) => {
    if (window.ROBUST_CART_DEV) {
      // eslint-disable-next-line no-console
      console.log("%cRobust Cart: " + msg, "background:#cd302b;color:#fff", this);
    }
  };

  // ---- network ----
  createAddToCartPromise = (formEl) => {
    const fd = new FormData(formEl);
    const data = JSON.stringify(Object.fromEntries(fd.entries())); // NOTE: arrays collapse; adapt if needed
    return fetch(this.cartAddUrl, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: data,
      credentials: "same-origin",
    });
  };

  createFetchCartPromise = () => fetch("/?section_id=robust-cart", { credentials: "same-origin" });

  createUpdateCartItemPromise = (lineItemKey, quantity) =>
    fetch(this.cartChangeUrl, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineItemKey, quantity }),
      credentials: "same-origin",
    });

  // ---- UI state ----
  openCart = () => {
    this.rootEl.classList.add("rc90--open");
    this.scrollPosition = window.scrollY || 0;
    this.lockScroll();
  };

  closeCart = () => {
    this.rootEl.classList.remove("rc90--open");
    this.unlockScroll();
  };

  // ---- refresh ----
  refreshCart = async () => {
    const box = this.rootEl.querySelector(".rc90-box");
    if (!box) return;

    const res = await this.createFetchCartPromise();
    const html = await res.text();

    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const nextBox = tmp.querySelector(".rc90-box");
    if (!nextBox) return;

    // Replace content only (preserves existing event on the box container)
    box.innerHTML = nextBox.innerHTML;

    // Re-wire inner buttons for the new content
    this.addInnerListeners();
  };

  // ---- item ops ----
  removeCartItem = async (lineItemKey) => {
    await this.createUpdateCartItemPromise(lineItemKey, 0);
    await this.refreshCart();
  };

  changeCartItemQuantity = async (lineItemKey, nextQty) => {
    await this.createUpdateCartItemPromise(lineItemKey, nextQty);
    await this.refreshCart();
  };

  // ---- bootstrap listeners ----
  addAllListeners = () => {
    this.addOpenCartListeners();
    this.addCartBoxListener();
    this.addFormSubmissionListenerOnce(); // delegated; only once
    this.addInnerListeners(); // re-wires parts that live inside .rc90-box
  };

  addInnerListeners = () => {
    this.addCloseCartListeners();     // overlay close (guarded to one-time)
    this.addItemRemoveListeners();    // content buttons
    this.addItemQuantityListeners();  // content +/- buttons
    if (typeof this.recommendedItemAddListener === "function") {
      this.recommendedItemAddListener(); // optional hook
    }
  };

  addCartBoxListener = () => {
    if (this._wired.boxStop) return;
    const box = this.rootEl.querySelector(".rc90-box");
    if (box) {
      box.addEventListener("click", (ev) => ev.stopPropagation());
      this._wired.boxStop = true;
    }
  };

  addOpenCartListeners = () => {
    if (this._wired.openLinks) return;
    this._wired.openLinks = true;

    this.log("Adding open click listeners");
    document.querySelectorAll(`[href="${this.cartUrl}"], [href="/cart"]`).forEach((a) => {
      a.addEventListener("click", (ev) => {
        ev.preventDefault();
        this.openCart();
      });
    });
  };

  addCloseCartListeners = () => {
    // Overlay click → close (wire once)
    if (!this._wired.overlayClose) {
      this.rootEl.addEventListener("click", this.closeCart);
      this._wired.overlayClose = true;
    }

    // Buttons inside refreshed content → re-wire every time
    this.rootEl
      .querySelectorAll(".rc90-header-close, .rc90-buttons-continue-shopping, .rc90-empty-inner-continue")
      .forEach((btn) => {
        btn.addEventListener("click", (ev) => {
          ev.preventDefault();
          this.closeCart();
        });
      });
  };

  /**
   * One-time delegated Add-to-Cart click handler.
   * Why: avoid re-attaching on every refresh.
   */
  addFormSubmissionListenerOnce = () => {
    if (this._wired.docAddToCart) return;
    this._wired.docAddToCart = true;

    document.addEventListener(
      "click",
      async (e) => {
        const t = /** @type {Element|null} */ (e.target instanceof Element ? e.target : null);
        if (!t) return;

        // Match the button or its descendants inside a matching form
        const addBtnSelector = `form[action="${this.cartAddUrl}"] button[name="add"]`;
        if (t.matches(`${addBtnSelector}, ${addBtnSelector} *`)) {
          e.preventDefault();
          const form = t.closest(`form[action="${this.cartAddUrl}"]`);
          if (!form) return;
          await this.createAddToCartPromise(form);
          await this.refreshCart();
          this.openCart();
        }
      },
      false
    );
  };

  addItemRemoveListeners = () => {
    this.rootEl.querySelectorAll(".rc90-item-main-header-remove").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.getAttribute("data-line-item-key");
        if (key) this.removeCartItem(key);
      });
    });
  };

  addItemQuantityListeners = () => {
    this.rootEl
      .querySelectorAll(".rc90-item-main-footer-quantity-plus, .rc90-item-main-footer-quantity-minus")
      .forEach((btn) => {
        const isPlus = btn.classList.contains("rc90-item-main-footer-quantity-plus");
        const key = btn.getAttribute("data-line-item-key");
        const qAttr = Number(btn.getAttribute("data-quantity"));
        if (!key || Number.isNaN(qAttr)) return;

        const next = isPlus ? qAttr + 1 : qAttr - 1;
        btn.addEventListener("click", async () => {
          await this.changeCartItemQuantity(key, next);
        });
      });
  };

  // ---- scroll lock ----
  lockScroll = () => {
    const y = this.scrollPosition || 0;
    const b = document.body;
    b.style.position = "fixed";
    b.style.top = `-${y}px`;
    b.style.left = "0";
    b.style.right = "0";
    b.style.width = "100%";
  };

  unlockScroll = () => {
    const b = document.body;
    const top = b.style.top;
    b.style.position = "";
    b.style.top = "";
    b.style.left = "";
    b.style.right = "";
    b.style.width = "";
    const y = parseInt(top || "0", 10);
    if (!Number.isNaN(y)) window.scrollTo(0, -y);
  };

  // ---- optional ----
  getCartJson = () => {
    const el = this.rootEl.querySelector(".robust-cart-json");
    const raw = el?.getAttribute("data-cart-json");
    return raw ? JSON.parse(raw) : undefined;
  };
}
